import { ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'

export interface ConfirmDialogProps {
  open: boolean
  title?: string
  /** Body shown above the typed-confirmation input. Plain text or rich node. */
  description?: ReactNode
  /**
   * Confirmation phrase the user must type to enable the destructive action.
   * Leave undefined for a simple "Are you sure?" with no typing requirement.
   */
  confirmPhrase?: string
  /** Optional helper text under the typed input. */
  confirmHelper?: string
  /** Label of the destructive button (default "Confirm"). */
  confirmLabel?: string
  /** Variant/severity of the destructive action: "error" (default) or "warning". */
  severity?: 'error' | 'warning'
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}

/**
 * A destructive-action confirmation modal with optional typed phrase.
 * If `confirmPhrase` is provided, the Confirm button stays disabled until
 * the user types it exactly. Use it for irreversible operations
 * (delete, force-cancel, reset, broadcast, retention).
 */
export default function ConfirmDialog({
  open,
  title = 'Confirm action',
  description,
  confirmPhrase,
  confirmHelper,
  confirmLabel = 'Confirm',
  severity = 'error',
  loading,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')

  useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  const phraseRequired = !!confirmPhrase
  const phraseMatches = !phraseRequired || typed.trim() === confirmPhrase
  const canConfirm = phraseMatches && !loading

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningIcon color={severity} />
          <Typography component="span" variant="h6">
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity={severity} variant="outlined">
            {description ??
              'This action cannot be undone. Are you sure you want to continue?'}
          </Alert>

          {phraseRequired && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Type{' '}
                <Box
                  component="code"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                  }}
                >
                  {confirmPhrase}
                </Box>{' '}
                to confirm.
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={confirmPhrase}
                error={typed.length > 0 && !phraseMatches}
                helperText={
                  typed.length > 0 && !phraseMatches
                    ? 'Phrase does not match'
                    : confirmHelper
                }
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={severity}
          disabled={!canConfirm}
          onClick={onConfirm}
        >
          {loading ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
